import {
  SelectOption as SelectComponentOption,
  PortalClientAccount,
  TransferFundId,
  TextFieldPresets,
  formattedCurrency,
  CURRENCY_SELECT_OPTIONS,
  US_CURRENCY_ONLY,
  IsTodayDate,
  Icon,
} from 'vopay';
import { ChangeEvent, JSX, useState } from 'react';
import moment from 'moment';
import { TransferFundRequest } from './types';
import styles from './TransferFund.module.scss';
import { useTranslation } from 'react-i18next';
import { FormikHelpers } from 'formik';

type SelectOption = SelectComponentOption<PortalClientAccount>;

export type TransferFundRows = {
  label: string;
  id: TransferFundId;
  require: boolean;
  preset: keyof TextFieldPresets | undefined;
  type: 'input' | 'select' | 'singleDate' | 'multiSelectDropdown' | 'multiSelector';
  placeholder: string;
  visible: (ctx: TransferFundRequest) => boolean;
  maxLength?: number;
  disableFiltering?: () => boolean;
  onSearch?: (term: string) => void;
  getSelectOptions?: () => { [key: string]: string };
  customOptions?: SelectOption[];
  getMultiSelectOptions?: (value: string) => SelectOption[];
  getMultiSelectValues?: (value: string) => SelectOption[];
  disabled?: boolean;
  onChangeCallBack?: (
    event: ChangeEvent<HTMLInputElement>,
    ctx: TransferFundRequest,
    setFieldValue: FormikHelpers<TransferFundRequest>['setFieldValue'],
    setFieldTouched: FormikHelpers<TransferFundRequest>['setFieldTouched'],
  ) => void;
  getSubElement?: (ctx: TransferFundRequest) => JSX.Element | undefined;
  renderOption?: (o: PortalClientAccount, args: { focused: boolean }) => React.ReactNode; // TODO: handle focused state manually
  renderMultiselectLabel?: (o: PortalClientAccount, args: { selected: boolean; focused: boolean }) => React.ReactNode;
  SelectLabel?: (ctx: TransferFundRequest) => string | undefined;
  onMultiselect?: (items: SelectOption[]) => void;
};

export const buildTransferFundRows = (
  clientAccounts: PortalClientAccount[],
  recipientAccounts: PortalClientAccount[],
  currency: string,
  transactionLabelOverrideEnabled: string,
  country: string,
  USDEFTSendEnabled: string,
  fetchDebitorListWithSearchTerm: (searchTerm: string | undefined) => void,
  setDebitorDetail: (details: PortalClientAccount) => void,
  setRecipientDetail: (details: PortalClientAccount) => void,
  debitorDetail: PortalClientAccount | undefined,
): TransferFundRows[] => {
  const clientAccountOptions = buildClientAccountOptions(clientAccounts);
  const recipientClientAccountOptions = buildClientAccountOptions(recipientAccounts);
  const [selectedRecipientAccounts, setSelectedRecipientAccounts] = useState<SelectOption[]>();
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts.TransferFunds.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const CurrencyOptions = {
    ...CURRENCY_SELECT_OPTIONS,
  };

  const CADOnly: { [key: string]: string } = {
    CAD: 'CAD',
  };

  const TransferTypeOptions: { [key: string]: string } = {
    Transfer: tGlobal('TransferTypeOptions.transfer'),
    FundAndTransfer: tGlobal('TransferTypeOptions.fundTransfer'),
    TransferAndWithdraw: tGlobal('TransferTypeOptions.transferWithdraw'),
    FundAndTransferAndWithdraw: tGlobal('TransferTypeOptions.fundTransferWithdraw'),
  };

  const FrequencyOptions = {
    monthly: tGlobal('FrequencyOptions.monthly'),
    weekly: tGlobal('FrequencyOptions.weekly'),
    'semi-monthly': tGlobal('FrequencyOptions.semiMonthly'),
    yearly: tGlobal('FrequencyOptions.annually'),
  };

  const ScheduleTypeOptions: { [key: string]: string } = {
    single: tGlobal('ScheduleType.single'),
    recurring: tGlobal('ScheduleType.recurring'),
  };

  const EndingOptions: { [key: string]: string } = {
    'End Date': tGlobal('EndingOptions.endDate'),
    Payments: tGlobal('EndingOptions.payments'),
  };

  const SemiMonthlyDateOptions = {
    SemiMonthFirstAndFifteenth: tGlobal('SemiMonthlyDateOptions.firstAndFifteenth'),
    SemiMonthFifteenthAndLast: tGlobal('SemiMonthlyDateOptions.fifteenthAndLast'),
  };

  return [
    {
      label: t('currencyLabel'),
      id: TransferFundId.Currency,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: () => true,
      getSelectOptions: () =>
        country === 'US' ? US_CURRENCY_ONLY : USDEFTSendEnabled === '0' ? CADOnly : CurrencyOptions,
    },
    {
      label: t('TransferType.label'),
      id: TransferFundId.TransferType,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: t('TransferType.placeholder'),
      visible: () => true,
      getSelectOptions: () => TransferTypeOptions,
      onChangeCallBack: (event, _ctx: TransferFundRequest, setFieldValue, setFieldTouched) => {
        const TransferType = event.target.value;

        if (TransferType === 'Transfer') {
          setFieldValue('RecipientClientAccountID', '');
          setFieldTouched('RecipientClientAccountID', false);
          setFieldValue('RecipientClientAccountIDSplit', '');
          setFieldTouched('RecipientClientAccountIDSplit', false);
        }
      },
    },
    {
      label: t('DebitorClientAccountID.label'),
      id: TransferFundId.DebitorClientAccountID,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: t('DebitorClientAccountID.placeholder'),
      visible: () => true,
      customOptions: clientAccountOptions,
      renderOption: (o, args) => {
        // show balance for the currently selected currency in each dropdown option
        const wallet = clientAccounts.find((a) => a.ClientAccountID === o.ClientAccountID && a.Currency === currency);

        return (
          <OptionLabel
            clientName={o.ClientDisplayName}
            currency={currency}
            balanceAccount={wallet?.AvailableFunds ?? 0}
            {...args}
          />
        );
      },
      disableFiltering: () => true,
      onSearch: (value: string) => {
        fetchDebitorListWithSearchTerm(value === '' ? undefined : value);
      },
      onChangeCallBack: (event) => {
        const DebitorClientAccountID = event.target.value;

        const clientAccount = clientAccounts.find((account) => account.ClientAccountID === DebitorClientAccountID);

        if (clientAccount) {
          setDebitorDetail(clientAccount);
        }
      },
      getSubElement: (ctx: TransferFundRequest) => {
        const wallet = clientAccounts.find(
          (a) => a.ClientAccountID === ctx.DebitorClientAccountID && a.Currency === ctx.Currency,
        );
        return ctx.DebitorClientAccountID !== '' ? (
          <div className={styles.AvailableBalance}>
            <span>
              {t('DebitorClientAccountID.fieldPrefix')} {ctx.Currency}{' '}
              {formattedCurrency((wallet?.AvailableFunds ?? 0).toString())}
            </span>
          </div>
        ) : undefined;
      },
      SelectLabel: () => {
        return debitorDetail?.ClientDisplayName;
      },
    },
    {
      label: t('RecipientClientAccountID.label'),
      id: TransferFundId.RecipientClientAccountID,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: t('RecipientClientAccountID.placeholder'),
      visible: (ctx: TransferFundRequest) => {
        if (ctx.TransferType === 'Transfer') {
          return true;
        }
        return false;
      },
      customOptions: recipientClientAccountOptions,
      renderOption: (o, args) => {
        const wallet = recipientAccounts.find(
          (a) => a.ClientAccountID === o.ClientAccountID && a.Currency === currency,
        );
        return (
          <OptionLabel
            clientName={o.ClientDisplayName}
            currency={currency}
            balanceAccount={wallet?.AvailableFunds ?? 0}
            {...args}
          />
        );
      },
      onChangeCallBack: (event) => {
        const RecipientClientAccountID = event.target.value;

        const clientAccount = recipientAccounts.find(
          (account) => account.ClientAccountID === RecipientClientAccountID,
        );

        if (clientAccount) {
          setRecipientDetail(clientAccount);
        }
      },
      getSubElement: (ctx: TransferFundRequest) => {
        const wallet = recipientAccounts.find(
          (a) => a.ClientAccountID === ctx.RecipientClientAccountID && a.Currency === ctx.Currency,
        );
        return ctx.RecipientClientAccountID !== '' ? (
          <div className={styles.AvailableBalance}>
            <span>
              {t('RecipientClientAccountID.fieldPrefix')} {ctx.Currency}{' '}
              {formattedCurrency((wallet?.AvailableFunds ?? 0).toString())}
            </span>
          </div>
        ) : undefined;
      },
      disableFiltering: () => true,
    },
    {
      label: t('RecipientClientAccountID.label'),
      id: TransferFundId.RecipientClientAccountID,
      require: true,
      preset: 'text',
      type: 'multiSelector',
      placeholder: t('RecipientClientAccountID.placeholder'),
      visible: (ctx: TransferFundRequest) => {
        if (ctx.TransferType !== 'Transfer') {
          return true;
        }

        return false;
      },
      onMultiselect: (items) => {
        setSelectedRecipientAccounts(items);
      },
      getMultiSelectValues: () => {
        return selectedRecipientAccounts || [];
      },
      getMultiSelectOptions: () => {
        const outputArray: SelectOption[] = recipientClientAccountOptions.map((opt) => ({
          ...opt,
          label: opt.ClientDisplayName,
          value: opt.ClientAccountID,
        }));
        return outputArray || [];
      },
      renderMultiselectLabel: (o, args) => {
        const wallet = recipientAccounts.find(
          (a) => a.ClientAccountID === o.ClientAccountID && a.Currency === currency,
        );
        return (
          <MultiSelectOptionLabel
            clientName={o.ClientDisplayName}
            currency={currency}
            balanceAccount={wallet?.AvailableFunds ?? 0}
            {...args}
          />
        );
      },
      getSubElement: (ctx: TransferFundRequest) => {
        if (ctx.RecipientClientAccountID.split('|').length > 1) return undefined;
        const clientAccount = recipientAccounts.find(
          (account) => account.ClientAccountID === ctx.RecipientClientAccountID && account.Currency === ctx.Currency,
        );
        return ctx.RecipientClientAccountID !== '' ? (
          <div className={styles.AvailableBalance}>
            <span>
              {t('RecipientClientAccountID.fieldPrefix')} {ctx.Currency}{' '}
              {formattedCurrency((clientAccount?.AvailableFunds ?? 0).toString())}
            </span>
          </div>
        ) : undefined;
      },
      disabled: (() => clientAccounts.length === 0)(),
    },
    {
      label: t('amountLabel'),
      id: TransferFundId.Amount,
      require: true,
      preset: 'currency',
      type: 'input',
      placeholder: '',
      visible: () => true,
    },
    {
      label: t('splitMethodLabel'),
      id: TransferFundId.SplitMethod,
      require: true,
      preset: 'text',
      type: 'multiSelectDropdown',
      placeholder: '',
      visible: (ctx: TransferFundRequest) => {
        let showSplitMethod = false;
        if (ctx.RecipientClientAccountID !== '' && ctx.TransferType !== '' && ctx.TransferType !== 'Transfer') {
          showSplitMethod = ctx.RecipientClientAccountID.split('|').length > 1;
        }
        return showSplitMethod;
      },
      getSelectOptions: () => ({}),
    },
    {
      label: t('scheduleTypeLabel'),
      id: TransferFundId.ScheduleType,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: TransferFundRequest) => ctx.TransferType !== 'Transfer',
      getSelectOptions: () => ScheduleTypeOptions,
    },
    {
      label: t('TakeFromAccountOn.label'),
      id: TransferFundId.TakeFromAccountOn,
      require: true,
      preset: 'date',
      type: 'singleDate',
      placeholder: t('TakeFromAccountOn.placeholder'),
      visible: (ctx: TransferFundRequest) => ctx.TransferType !== 'Transfer' && ctx.ScheduleType === 'single',
    },
    {
      label: t('StartDate.label'),
      id: TransferFundId.StartDate,
      require: true,
      preset: 'date',
      type: 'singleDate',
      placeholder: t('StartDate.placeholder'),
      visible: (ctx: TransferFundRequest) => ctx.TransferType !== 'Transfer' && ctx.ScheduleType === 'recurring',
    },
    {
      label: t('frequencyLabel'),
      id: TransferFundId.Frequency,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: TransferFundRequest) => ctx.TransferType !== 'Transfer' && ctx.ScheduleType === 'recurring',
      getSelectOptions: () => FrequencyOptions,
    },
    {
      label: t('semiMonthlyDatesLabel'),
      id: TransferFundId.SemiMonthlyDates,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: TransferFundRequest) =>
        ctx.TransferType !== 'Transfer' && ctx.ScheduleType === 'recurring' && ctx.Frequency === 'semi-monthly',
      getSelectOptions: () => SemiMonthlyDateOptions,
    },
    {
      label: t('endingLabel'),
      id: TransferFundId.Ending,
      require: false,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: TransferFundRequest) => ctx.TransferType !== 'Transfer' && ctx.ScheduleType === 'recurring',
      getSelectOptions: () => EndingOptions,
    },
    {
      label: t('EndDate.label'),
      id: TransferFundId.EndDate,
      require: true,
      preset: 'date',
      type: 'singleDate',
      placeholder: t('EndDate.placeholder'),
      visible: (ctx: TransferFundRequest) => ctx.ScheduleType === 'recurring' && ctx.Ending === 'End Date',
    },
    {
      label: t('endingAfterPaymentsLabel'),
      id: TransferFundId.EndingAfterPayments,
      require: true,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: TransferFundRequest) => ctx.Ending === 'Payments',
    },
    {
      label: t('Description.label'),
      id: TransferFundId.Description,
      require: true,
      preset: 'text',
      type: 'input',
      placeholder: t('Description.placeholder'),
      visible: (ctx: TransferFundRequest) =>
        ctx.TransferType !== 'Transfer' &&
        (ctx.ScheduleType === 'recurring' ||
          (ctx.TakeFromAccountOn !== '' && !IsTodayDate(moment(Number(ctx.TakeFromAccountOn)).format('YYYY-MM-DD')))),
    },
    {
      label: t('clientReferenceNumberLabel'),
      id: TransferFundId.ClientReferenceNumber,
      require: false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => true,
    },
    {
      label: t('transactionLabel'),
      id: TransferFundId.TransactionLabel,
      require: false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => transactionLabelOverrideEnabled === '1',
      maxLength: 30,
    },
    {
      label: t('notesLabel'),
      id: TransferFundId.Notes,
      require: false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => true,
    },
  ];

  function buildClientAccountOptions(clientAccountList: PortalClientAccount[]) {
    const options: SelectOption[] = [];
    /*
     * forEach was used instead of reduce because when the list got large around 30000 client
     * accounts the overhead of maintaining the accumulator caused significant performance issues
     * when compared to forEach
     *
     * No IsActive check needed: the portal endpoint only returns active accounts.
     */
    clientAccountList.forEach((data) => {
      if (!options.some((o) => o.ClientAccountID === data.ClientAccountID)) {
        options.push({
          ...data,
          label: data.ClientDisplayName,
          value: data.ClientAccountID,
        });
      }
    });
    return options;
  }
};

function OptionLabel({
  clientName,
  currency,
  balanceAccount,
  focused,
}: {
  clientName: string;
  currency: string;
  balanceAccount: number;
  focused: boolean;
}) {
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts.TransferFunds.Form' });
  return (
    <div className={[styles.optionLabel, focused ? styles.focused : ''].join(' ')}>
      <div className={styles.optionLabelContent}>
        <div>{clientName}</div>
        <div className={styles.formatAvailableBalance}>
          {t('optionLabelPrefix', { currency, amount: formattedCurrency(balanceAccount.toString()) })}
        </div>
      </div>
    </div>
  );
}

function MultiSelectOptionLabel({
  clientName,
  currency,
  balanceAccount,
  selected,
  focused,
}: {
  clientName: string;
  currency: string;
  balanceAccount: number;
  selected: boolean;
  focused: boolean;
}) {
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts.TransferFunds.Form' });
  return (
    <div className={[styles.optionLabel, focused ? styles.focused : ''].join(' ')}>
      <div className={styles.optionLabelContent}>
        <div className={styles.optionLabelTop}>
          {selected && <Icon icon="check" color="primary-dark" width="12px" height="10px" size="sm" />}
          <div>{clientName}</div>
        </div>
        <div className={styles.formatAvailableBalance}>
          {t('optionLabelPrefix', { currency, amount: formattedCurrency(balanceAccount.toString()) })}
        </div>
      </div>
    </div>
  );
}
